/**
 * resolveBusinessModel — client-side niche → business_model resolver.
 *
 * Mirrors the backend MI catalog logic so we can render
 * model-appropriate copy in modals BEFORE the user has saved their
 * business profile. Whenever the backend returns a `business_model`
 * field directly, prefer that — this helper is for the bridge
 * period (e.g. Monetization Plan modal which is called before
 * profile creation).
 *
 * Buckets:
 *   - "service_local"  → handyman, cleaning, lawn, hvac, plumbing,
 *                         electric, roofing, pet sitting, moving,
 *                         pressure washing, junk removal, painting,
 *                         landscaping, mobile car wash, food truck,
 *                         barber, beauty salon, in-home daycare, …
 *   - "professional"   → coach, consultant, accountant, lawyer,
 *                         therapist, financial advisor, real estate
 *                         agent, fitness trainer (1:1) …
 *   - "ecommerce"      → physical products, dropshipping, print on
 *                         demand, handmade goods, etsy, …
 *   - "creator"        → content creator, influencer, podcaster,
 *                         youtuber, newsletter, blog, …
 *   - "digital_product"→ course, ebook, template, software, SaaS,
 *                         membership, paid community, …
 *   - "unknown"        → default when we can't classify
 *
 * Keep this pure and dependency-free.
 */

const SERVICE_LOCAL_KW = [
  'handyman', 'handywoman', 'handy',
  'cleaning', 'house clean', 'maid', 'janitor',
  'lawn', 'landscap', 'mowing', 'yard', 'tree service',
  'hvac', 'plumb', 'electric', 'roof', 'gutter',
  'pet sit', 'dog walk', 'dog board', 'dog train',
  'moving', 'mover', 'haul', 'junk removal',
  'pressure wash', 'power wash', 'window clean',
  'paint', 'house paint', 'auto detail', 'car wash',
  'mobile mechanic', 'auto repair', 'tow',
  'food truck', 'catering', 'meal prep',
  'barber', 'beauty salon', 'nail tech', 'eyelash',
  'in-home daycare', 'babysit', 'nanny',
  'home repair', 'remodel', 'contractor',
  'tutor', 'piano lesson', 'guitar lesson',
];

const PROFESSIONAL_KW = [
  'coach', 'consultant', 'consulting',
  'accountant', 'cpa', 'bookkeep',
  'lawyer', 'attorney', 'paralegal',
  'therapist', 'therapy', 'counsel',
  'financial advisor', 'wealth manager',
  'real estate agent', 'realtor',
  'personal trainer', 'fitness coach',
  'mindset coach', 'life coach', 'business coach',
];

const ECOMMERCE_KW = [
  'ecommerce', 'e-commerce',
  'dropship', 'print on demand', 'pod ',
  'shopify', 'etsy', 'amazon fba',
  'physical product', 'handmade',
  'jewelry', 'candle', 'soap',
  'apparel', 'merch', 't-shirt',
  'storefront', 'retail',
];

const DIGITAL_PRODUCT_KW = [
  'digital product', 'course creator',
  'online course', 'cohort',
  'ebook', 'e-book',
  'template', 'notion template', 'spreadsheet',
  'saas', 'software', 'plugin',
  'membership', 'paid community',
  'newsletter monetization', 'paid newsletter',
];

const CREATOR_KW = [
  'creator', 'influencer', 'podcaster',
  'youtuber', 'tiktoker',
  'blogger', 'blog',
  'newsletter', 'substack',
  'content creator',
];

const matches = (text, list) =>
  list.some((kw) => text.indexOf(kw) !== -1);

export const resolveBusinessModel = (input) => {
  if (!input) return 'unknown';
  const niche = (
    typeof input === 'string'
      ? input
      : (input.niche || input.business_idea || input.business_type || '')
  ).toString().toLowerCase().trim();
  if (!niche) return 'unknown';

  if (matches(niche, SERVICE_LOCAL_KW))    return 'service_local';
  if (matches(niche, PROFESSIONAL_KW))     return 'professional';
  if (matches(niche, ECOMMERCE_KW))        return 'ecommerce';
  if (matches(niche, DIGITAL_PRODUCT_KW))  return 'digital_product';
  if (matches(niche, CREATOR_KW))          return 'creator';
  return 'unknown';
};

/**
 * Headings that adapt to the user's business model for the
 * "How to Turn ... Into Sales" bridge block on the Monetization
 * Plan modal. Static copy is wrong for service businesses (a
 * handyman doesn't sell "content" — they sell visits).
 */
export const bridgeHeadingFor = (model) => {
  switch (model) {
    case 'service_local':
      return 'How to Turn Local Visibility Into Customers';
    case 'professional':
      return 'How to Turn Expertise Into Clients';
    case 'ecommerce':
      return 'How to Turn Products Into Sales';
    case 'creator':
    case 'digital_product':
      return 'How to Turn Content Into Sales';
    default:
      return null; // unknown → caller hides the block
  }
};

/**
 * Heading for the "Products I'd Recommend For You" grid. For
 * service_local and professional models the literal word
 * "products" is misleading — they don't sell SKUs. Caller can
 * also hide the entire grid in those cases.
 */
export const recommendationsHeadingFor = (model) => {
  switch (model) {
    case 'service_local':
      return 'Customer Acquisition Plays for Your Business';
    case 'professional':
      return 'Client Acquisition Plays for Your Practice';
    case 'ecommerce':
      return 'Products I\u2019d Recommend For You';
    case 'creator':
    case 'digital_product':
      return 'Products I\u2019d Recommend For You';
    default:
      return null;
  }
};

/**
 * Whether the "product ideas" grid (physical/digital SKUs the
 * monetization service suggests) actually makes sense for the
 * user's business model. Local services and 1:1 professionals
 * sell time, not SKUs — hide the grid for them.
 */
export const showProductIdeasFor = (model) =>
  model === 'ecommerce' ||
  model === 'creator' ||
  model === 'digital_product' ||
  model === 'unknown';

export default resolveBusinessModel;
